import React from "react";
import { useFormContext } from "react-hook-form";
import {
  ListGroupItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Container,
  Row,
  Col,
  Card,
  CardImg,
  CardBody,
  CardTitle,
  CardText,
  Button,
  ListGroup,
} from "reactstrap";
import PropTypes from "prop-types";
import {
  upload as uploadFile,
  uploadSuccess,
  reset as resetFileUpload,
  startCrop,
  endCrop,
} from "../../../redux/cop/artworks";

import icon_crop from "../../../assets/images/icons/crop.svg";
import icon_filesize from "../../../assets/images/icons/filesize.svg";

import FormattedNumber from "../../FormattedNumber";
import calculateOptimizedSizeProposal from "./calculateOptimizedSizeProposal";
import DataLayerHelper from "../../../utils/dataLayer";
import { useDispatch, useSelector } from "react-redux";

const FileSizeModal = (
  { proposeOptimizedSizes, handleModalFileUpload },
  context
) => {
  const dispatch = useDispatch();
  const { setValue, watch } = useFormContext();
  const width = watch("width");
  const height = watch("height");
  const fileRatio = watch("fileRatio");
  const artworkId = watch("artworkId");

  const { artwork, cropping } = useSelector((state) => ({
    artwork: state.artworks.byIdDictionary[artworkId],
    cropping: state.artworks.cropping,
  }));

  const isAspectRatioMismatch = () => {
    if (!artwork?.data?.attributes) return false;
    const { width: awWidth, height: awHeight } = artwork.data.attributes;
    const aspectDifference = Math.abs(awWidth / awHeight - width / height);
    return (
      !cropping?.active &&
      artwork?.data?.attributes?.preview &&
      aspectDifference > 0.75
    );
  };

  const handleCropFile = () => {
    dispatch(startCrop());
    DataLayerHelper.addEventWithOrigin(
      "behaviour",
      "file editor",
      "size missmatch",
      "start cropping"
    );
  };

  const optimizedSizes = calculateOptimizedSizeProposal(
    artwork?.data?.attributes,
    width,
    height
  );

  const handleAdaptFileSizes = (size_mode) => {
    if (size_mode === "by_width") {
      setValue("width", optimizedSizes.alt_width_by_width);
      setValue("height", optimizedSizes.alt_height_by_width);
    } else if (size_mode === "by_height") {
      setValue("width", optimizedSizes.alt_width_by_height);
      setValue("height", optimizedSizes.alt_height_by_height);
    } else {
      setValue("width", optimizedSizes.width);
      setValue("height", optimizedSizes.height);
    }
    setValue("fileRatio", optimizedSizes.ratio);
    setValue("keepRatio", true);

    DataLayerHelper.addEventWithOrigin(
      "behaviour",
      "file editor",
      "size missmatch",
      "use filesize"
    );
  };

  const renderSizeOption = (label, width, height, action) => (
    <ListGroupItem tag="button" className="btn-link" action onClick={action}>
      <FormattedNumber
        value={Math.round(width * 100) / 100}
        language="en"
        unit="mm"
        decimals={0}
      />
      &nbsp;&times;&nbsp;
      <FormattedNumber
        value={Math.round(height * 100) / 100}
        language="en"
        unit="mm"
        decimals={0}
      />
    </ListGroupItem>
  );

  return (
    <Modal
      fade={false}
      className="file-size-modal"
      size="lg"
      isOpen={isAspectRatioMismatch()}
      centered
    >
      <ModalHeader>
        {context.t("Your file has a different ratio than the specified size!")}
      </ModalHeader>
      <ModalBody>
        <p>
          {context.t(
            "Attention: The ratio of your file unfortunately does not match the chosen size. What should we do?"
          )}
        </p>
        <Container>
          <Row>
            <Col md={6}>
              <Card>
                <CardImg height="60" src={icon_crop} className="my-3" />
                <CardBody>
                  <CardTitle className="h5">
                    {context.t("Crop file to selected sizes")}
                  </CardTitle>
                  <CardText className="semi-small">
                    {context.t(
                      "If you do not have an exact matching file, you can simply mark the appropriate image section. We only print the selected area for your product."
                    )}
                  </CardText>
                  <Button color="primary" onClick={handleCropFile}>
                    {context.t("Crop image")}
                  </Button>
                </CardBody>
              </Card>
            </Col>
            {proposeOptimizedSizes && (
              <Col md={6}>
                <Card>
                  <CardImg height="60" src={icon_filesize} className="my-3" />
                  <CardBody>
                    <CardTitle className="h5">
                      {context.t(
                        "Adjust the size to the aspect ratio of your file"
                      )}
                    </CardTitle>
                    <CardText className="semi-small">
                      {context.t(
                        "Based on the measurements we have calculated from your file, our suggested size is:"
                      )}
                    </CardText>
                    <ListGroup>
                      {renderSizeOption(
                        "By Width",
                        optimizedSizes.alt_width_by_width,
                        optimizedSizes.alt_height_by_width,
                        () => handleAdaptFileSizes("by_width")
                      )}
                      {renderSizeOption(
                        "By Height",
                        optimizedSizes.alt_width_by_height,
                        optimizedSizes.alt_height_by_height,
                        () => handleAdaptFileSizes("by_height")
                      )}
                      {renderSizeOption(
                        "Original",
                        optimizedSizes.width,
                        optimizedSizes.height,
                        handleAdaptFileSizes
                      )}
                    </ListGroup>
                    <em className="text-remark">
                      <strong>{context.t("Note:")}</strong>{" "}
                      {context.t("You can still change the size later")}
                    </em>
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>
        </Container>
      </ModalBody>
      <ModalFooter style={{ background: "#efefef" }}>
        <p className="small my-0">
          {context.t(
            "If you have a file that fits exactly to the dimensions you want, you can of course upload them."
          )}
          &nbsp;
          <label htmlFor="modal_file_upload" className="btn-link">
            {context.t("Upload File")}
          </label>
          <input
            type="file"
            id="modal_file_upload"
            className="fileeditor__custom-file-upload-input"
            onChange={handleModalFileUpload}
          />
        </p>
      </ModalFooter>
    </Modal>
  );
};

/**  define proptype for the 'translation' function  */
FileSizeModal.contextTypes = {
  t: PropTypes.func,
};

export default FileSizeModal;
